import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";

import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import { useMutationWithError } from "../../../../utils/hooks/useMutationWithError";
import { JoinBookingInput } from "./__generated__/useJoinExistingBookingMutation.graphql";

type SuccessCallback = () => void;
type MutationResponse = {
  responsePayload?: {
    success?: boolean;
  } | null;
};

export const useJoinExistingBooking = () => {
  const [joinExistingBooking, isCreating] = useMutationWithError(
    useJoinExistingBookingMutationString
  );

  const { showToast } = useToast();

  const onCompleted = (
    successCallback: SuccessCallback,
    res: MutationResponse
  ) => {
    if (res?.responsePayload?.success && successCallback) {
      successCallback();
    }
  };

  const onCompletedErrorCustom = (errors: any) => {
    showToast({
      description: t`Sorry. An error occurred while joining that booking.`,
      semanticStyle: "error",
      toastId: "create-booking-error",
    });

    return [];
  };

  const handleJoinExistingBooking = (
    input: JoinBookingInput,
    successCallback?: () => void
  ) => {
    joinExistingBooking({
      variables: { input },
      onCompleted: (res: any) =>
        successCallback ? onCompleted(successCallback, res) : undefined,
      onCompletedErrorCustom: (errors: any) => onCompletedErrorCustom(errors),
    });
  };

  return [handleJoinExistingBooking, isCreating] as const;
};

const useJoinExistingBookingMutationString = graphql`
  mutation useJoinExistingBookingMutation($input: JoinBookingInput!) {
    responsePayload: joinBooking(input: $input) {
      errors {
        field
        code
        message
      }
      success
      booking {
        id
        canUpdate
        writeAccess
        startSlot {
          ... on TimeSlot {
            id
            capacity(bookingType: STANDARD)
            usedCapacity
          }
        }
        rounds {
          edges {
            node {
              id
              __typename
              cartNumber
              courseLayout {
                id
                code
                name
              }
              transportationType {
                id
                code
                customCode
                name
              }
              secondaryTransportationType {
                id
                code
                customCode
                name
              }
              player {
                email
                firstName
                lastName
                id
                profileImage {
                  # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
                  publicId: renderingKey
                }
                ... on Member {
                  memberGroups {
                    id
                  }
                }
              }
              ... on MemberRound {
                paymentStatus
                nonPlayingHost
              }
              ... on GuestRound {
                paymentStatus
                host {
                  id
                }
              }
            }
          }
        }
      }
    }
  }
`;
